import React, { useState } from 'react';
import styled from 'styled-components';
import * as size from '@constant/size';
import * as color from '@constant/color';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import DesignOption from '@design-system/option';
import TextButton from '@design-system/text-button';
import { ETheme } from '@datatype/type';
import { EApiLanguageCode, EApiLanguageLevel, EApiStudyTimeGoal } from '@datatype/iyagigo/api-type';
import { useStudyConfiguration } from '@hook/iyagigo/useStudent';
import { useModifyStudentMutation } from '@hook/iyagigo/useApi';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import { LOCAL_STORAGE_STUDENT_KEY } from '@config/auth';
import {
  IYAGIGO_STUDY_PLAN_LANGUAGE_LEVEL_OPTIONS,
  IYAGIGO_STUDY_PLAN_STUDY_TIME_GOAL_OPTIONS,
} from '@constant/iyagigo/variable';

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: ${size.MEDIUM_MODAL_WIDTH};
`;

const OptionGroupContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 6px;
  width: 100%;
`;

const OptionListContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 5px;
  width: 100%;
`;

const StudyPlanModal: React.FC = () => {
  const dispatch = useTypedDispatch();
  const studyConfiguration = useStudyConfiguration();
  const modifyStudentMutation = useModifyStudentMutation();
  const studyLanguage = studyConfiguration?.studyLanguage ?? EApiLanguageCode.JAPANESE;
  const currentLevel =
    studyLanguage === EApiLanguageCode.ENGLISH
      ? studyConfiguration?.englishLanguageLevel
      : studyConfiguration?.japaneseLanguageLevel;
  const currentStudyTimeGoal =
    studyLanguage === EApiLanguageCode.ENGLISH
      ? studyConfiguration?.englishStudyTimeGoal
      : studyConfiguration?.japaneseStudyTimeGoal;
  const [selectedLevel, setSelectedLevel] = useState<EApiLanguageLevel | null>(currentLevel ?? null);
  const [selectedStudyTimeGoal, setSelectedStudyTimeGoal] = useState<EApiStudyTimeGoal | null>(
    currentStudyTimeGoal ?? null,
  );
  const [preferenceStep, setPreferenceStep] = useState<'level' | 'study-time-goal'>('level');

  const isLevelStep = preferenceStep === 'level';

  const handleSave = async () => {
    const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY);
    if (!studentKey || !selectedLevel || !selectedStudyTimeGoal) return;
    await modifyStudentMutation.mutateAsync({
      studentKey,
      studyLanguage,
      languageLevel: selectedLevel,
      studyTimeGoal: selectedStudyTimeGoal,
    });
    dispatch(modalActions.hideModal());
  };

  return (
    <Wrapper>
      <TextBlock
        text={isLevelStep ? '학습 수준을 선택해주세요' : '하루 학습 시간을 선택해주세요'}
        design={{ text: { size: 'medium', color: color.BLACK_2, bold: false }, box: { alignX: 'center' } }}
      />
      <Gap height={size.MARGIN_2} />
      {isLevelStep ? (
        <OptionGroupContainer>
          <OptionListContainer>
            {IYAGIGO_STUDY_PLAN_LANGUAGE_LEVEL_OPTIONS.map((option) => (
              <DesignOption
                key={option.value}
                theme={ETheme.BROWN}
                align='left'
                width='100%'
                label={option.text}
                value={option.description}
                selected={option.value === selectedLevel}
                handleClick={() => setSelectedLevel(option.value)}
              />
            ))}
          </OptionListContainer>
        </OptionGroupContainer>
      ) : (
        <OptionGroupContainer>
          <OptionListContainer>
            {IYAGIGO_STUDY_PLAN_STUDY_TIME_GOAL_OPTIONS.map((option) => (
              <DesignOption
                key={option.value}
                theme={ETheme.BROWN}
                align='left'
                width='100%'
                label={option.text}
                value={option.description}
                selected={option.value === selectedStudyTimeGoal}
                handleClick={() => setSelectedStudyTimeGoal(option.value)}
              />
            ))}
          </OptionListContainer>
        </OptionGroupContainer>
      )}
      <Gap height={size.MARGIN_2} />
      <TextButton
        text={isLevelStep ? '다음' : '저장하기'}
        theme={ETheme.BROWN}
        size='medium'
        shape='pill'
        width='100%'
        disabled={isLevelStep ? selectedLevel === null : selectedStudyTimeGoal === null}
        handleClick={isLevelStep ? () => setPreferenceStep('study-time-goal') : handleSave}
      />
    </Wrapper>
  );
};

export default StudyPlanModal;
